import {
  NOTIFICATION_KIND_LABELS,
  type NotificationKind,
  notificationAudienceOf,
} from '@repo/api-contracts';
import { type ReactElement, useId } from 'react';
import { cn } from '@/shared/ui';
import { useUpdateNotificationKindSetting } from '../model/use-update-notification-kind-setting';

interface NotificationKindToggleProps {
  readonly kind: NotificationKind;
  /** 지금 켜져 있는지 — 바꾼 뒤 설정을 다시 읽으면 새 값으로 바뀐다. */
  readonly enabled: boolean;
}

/** 고객 메일을 끌 때의 확인창 문구(알림 설계 §6-2) — 꺼 둔 동안 고객은 그 안내를 받지 못한다. */
const customerOffConfirmMessage = (label: string): string =>
  `"${label}" 메일을 끄시겠습니까?\n\n고객에게 안내가 가지 않습니다. 꺼 둔 동안 보낼 메일은 발송 이력에 "꺼진 알림"으로 남습니다.`;

/**
 * 알림 종류 하나의 ON/OFF 스위치(알림 설계 N3). 스위치 이름은 종류 한글 이름이다. 고객 메일을 끌 때만 확인창을 띄운다.
 * 운영자 메일을 끄거나 꺼진 메일을 켤 때는 바로 보낸다. 요청과 설정 재조회가 끝날 때까지 스위치를 잠근다.
 */
export const NotificationKindToggle = ({
  kind,
  enabled,
}: NotificationKindToggleProps): ReactElement => {
  const label = NOTIFICATION_KIND_LABELS[kind];
  const errorId = useId();
  const { updateKindSetting, isPending, error } = useUpdateNotificationKindSetting();

  const handleClick = (): void => {
    const nextEnabled = !enabled;
    const turnsOffCustomerMail = !nextEnabled && notificationAudienceOf(kind) === 'CUSTOMER';

    if (turnsOffCustomerMail && !window.confirm(customerOffConfirmMessage(label))) {
      return;
    }

    void updateKindSetting({ kind, enabled: nextEnabled });
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <span className="flex items-center gap-2">
        <span aria-hidden="true" className="text-xs text-muted-foreground">
          {enabled ? '켜짐' : '꺼짐'}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={label}
          aria-busy={isPending}
          aria-describedby={error ? errorId : undefined}
          disabled={isPending}
          onClick={handleClick}
          className={cn(
            'relative h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            enabled ? 'bg-primary' : 'bg-muted-foreground/30',
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-0.5 left-0.5 size-5 rounded-full bg-background shadow transition-transform',
              enabled && 'translate-x-5',
            )}
          />
        </button>
      </span>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
